import { Collapse } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import FeatureToggle from '@components/FeatureToggle';
import { sendSuccess } from '@components/Noti';
import Slider from '@components/Slider';
import Title from '@components/Title';
import { $curScene, $sceneMapApi } from '@stores/dataStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import { $loadedScene } from '@stores/loadedStore';
import { $viewMode } from '@stores/viewModeStores';
import { MatrixFovType, SceneType } from '@types';
import { useStore } from 'effector-react';
import { toFlag, toPercent } from 'pano-utils';
import { useEffect, useState } from 'react';
import { showSceneModal } from '../../category';
import { loadScene } from '../../player';
import { onFlatFovChanged, viewDefault } from '../../view';
import SidebarApplyTo from '../components/SidebarApplyTo';
import SidebarHeader from '../components/SidebarHeader';
import InitialView from './InitialView';

const items: DropdownItem<MatrixFovType>[] = [
  { id: MatrixFovType.fullscreen, name: '全部显示' },
  { id: MatrixFovType.fill, name: '铺满屏幕' },
  { id: MatrixFovType.custom, name: '自定义初始视角' },
];

function MatrixView() {
  const data = useStore($loadedScene)!;
  const { view, id } = data;
  const krpPlayer = MainPlayer.get();
  const [fov, setFov] = useState<{ min: number; max: number } | null>(null);

  const zoomEnabled = !(view.disableZoom ?? 0);

  useEffect(() => {
    setFov({
      min: krpPlayer?.getView().fovmin!,
      max: krpPlayer?.getView().fovmax!,
    });
  }, [id]);

  useEffect(() => {
    return onFlatFovChanged.watch((x) => {
      if (!x.fovmin || !x.fovmax) return;
      setFov({
        min: x.fovmin,
        max: x.fovmax,
      });
      if (x.needUpdateFov) {
        $sceneMapApi.updateScene({
          id,
          updater: (draft) => {
            draft.view.fov = krpPlayer?.getView().fov!;
          },
        });
      }
    });
  }, []);

  useEffect(() => {
    return $viewMode.updates.watch(() => {
      if (view.flatFovType === MatrixFovType.fullscreen) {
        setTimeout(() => {
          krpPlayer?.updateView({ flatFovType: view.flatFovType });
        }, 100);
      }
    });
  }, [view.flatFovType]);

  const handleTypeChange = (k: MatrixFovType) => {
    krpPlayer?.updateView({
      ...view,
      flatFovType: k,
      ...(k === MatrixFovType.custom && { disableZoom: 0 }),
    });
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.view.flatFovType = k;
        draft.view.fov = krpPlayer?.getView().fov!;
        draft.view.hLookAt = krpPlayer?.getView().hlookat!;
        draft.view.vLookAt = krpPlayer?.getView().vlookat!;
        if (k === MatrixFovType.custom) {
          draft.view.disableZoom = 0;
        }
      },
    });
  };

  const handleApplyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      subTitle: '注：只能应用到同类型场景中',
      allowedTypes: [SceneType.matrix],
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        const scene = draft.get(_id);
        if (scene) {
          scene.view = view;
        }
      });
    });
    sendSuccess('视角设置已应用到所选场景');
  };

  return (
    <>
      <SidebarHeader
        title={
          <div className="flex">
            <Title>视角</Title>
            <span className="ml-5 self-end">（矩阵）</span>
          </div>
        }
        onResetClick={() => {
          $sceneMapApi.updateScene({
            id,
            updater: (draft) => {
              draft.view = viewDefault;
            },
          });
          loadScene({
            player: krpPlayer,
            data: $curScene.getState(),
          });
        }}
      />
      <InitialView
        data={data}
        onTurnViewClick={() => {
          krpPlayer?.lookTo({
            fov: view.fov,
            hlookat: view.hLookAt,
            vlookat: view.vLookAt,
          });
        }}
      />
      <Collapse title="默认视角">
        <Dropdown
          items={items}
          selected={view.flatFovType}
          onChange={handleTypeChange}
        />
      </Collapse>
      {(view.flatFovType === MatrixFovType.fullscreen ||
        view.flatFovType === MatrixFovType.fill) && (
        <Collapse title="默认视角">
          <FeatureToggle
            isOn={zoomEnabled}
            onToggle={(bool: boolean): void => {
              // krpPlayer更新disableZoom时，需要传整个view
              krpPlayer?.updateView({ ...view, disableZoom: toFlag(!bool) });
              $sceneMapApi.updateScene({
                id,
                updater: (draft) => {
                  draft.view.disableZoom = toFlag(!bool);
                },
              });
            }}
            title={'开启缩放操作'}
          />
        </Collapse>
      )}
      {zoomEnabled && (
        <Collapse title="图片放大设置">
          <Slider
            min={0.5}
            max={3}
            value={view.maxPixelZoom}
            valToString={(val) => toPercent(val, 0)}
            onChange={(val) => {
              krpPlayer?.updateView({
                maxpixelzoom: val,
                zoomtomaxpixelzoom: true,
              });
              $sceneMapApi.updateScene({
                id,
                updater: (draft) => {
                  draft.view.maxPixelZoom = val;
                },
              });
            }}
          >
            <Slider.Header title="最大放大倍数" />
            <Slider.Bar step={0.01} />
            <Slider.Control step={0.01} />
          </Slider>
        </Collapse>
      )}
      {data.view.flatFovType !== MatrixFovType.fullscreen &&
        fov &&
        zoomEnabled && (
          <Collapse title="视角（FOV）范围">
            <Slider
              min={fov.min}
              max={fov.max}
              value={view.fov}
              valToString={(val) => {
                if (val === fov.min) return '最近';
                if (val === fov.max) return '最远';
                return '';
              }}
              onChange={(val) => {
                krpPlayer?.updateView({ fov: val });
                $sceneMapApi.updateScene({
                  id,
                  updater: (draft) => {
                    draft.view.fov = val;
                  },
                });
              }}
            >
              <Slider.Bar step={0.0001} />
              <Slider.Footer />
            </Slider>
          </Collapse>
        )}
      {data.view.flatFovType === MatrixFovType.fullscreen && (
        <SidebarApplyTo onClick={handleApplyTo} />
      )}
    </>
  );
}

export default MatrixView;
